import {ArrayUtils, ConfigObjectType, HierarchyMetadata} from '@wallaby-ui/core';
import {sortBy} from 'lodash';
import React, {useMemo} from 'react';
import {useOrganizations, useOrganizationsByOrgSecurity} from 'src/js/containers';
import {WallabyCoreSchema} from 'types/js';

import {HierarchyFilterProps, LightHierarchyFilter} from './LightHierarchyFilter';

type Organization = WallabyCoreSchema.components['schemas']['OrganizationFilterDto'] & HierarchyMetadata;
export type LightOrganizationFilter2Props = Omit<HierarchyFilterProps<Organization>, 'options'> & {
  appConfig: ConfigObjectType;
  getOptionDisabled?: (v: Organization) => boolean;
  /**
   * When true, the organizations the current user is permitted to see (org-security)
   * are listed first as a "preferred" group, separated from the rest by a divider.
   * The remaining organizations stay visible and selectable.
   */
  preferOrgSecurityDepartments?: boolean;
};
type LightOrganizationFilterDataProps = {
  appConfig: ConfigObjectType;
  getOptionDisabled?: (v: Organization) => boolean;
  preferOrgSecurity?: boolean;
};

type GroupingInput = {id: number; parentId?: number; isSoloRootNode?: boolean};

/**
 * For a hierarchical organization list and the set of org ids the user is
 * permitted to see, computes:
 *  - `preferredIdSet`: every node that is itself permitted OR has a permitted
 *    descendant (so a whole hospital subtree is "preferred" when its departments are).
 *  - `topLevelIdSet`: the nodes that render at the outermost level, i.e. the
 *    hierarchy roots with solo-root container nodes (which the dropdown hides)
 *    expanded into their children — this mirrors what `ignoreOption` does at render time.
 */
function computeOrganizationGrouping(organizations: GroupingInput[], myIds: Set<number>) {
  const byId = new Map<number, GroupingInput>();
  const childrenByParent = new Map<number, number[]>();
  organizations.forEach((o) => {
    byId.set(o.id, o);
    if (o.parentId != null) {
      const siblings = childrenByParent.get(o.parentId) ?? [];
      siblings.push(o.id);
      childrenByParent.set(o.parentId, siblings);
    }
  });

  const preferredCache = new Map<number, boolean>();
  const computePreferred = (id: number): boolean => {
    const cached = preferredCache.get(id);
    if (cached !== undefined) return cached;
    let preferred = myIds.has(id);
    (childrenByParent.get(id) ?? []).forEach((childId) => {
      if (computePreferred(childId)) preferred = true;
    });
    preferredCache.set(id, preferred);
    return preferred;
  };
  const preferredIdSet = new Set<number>();
  organizations.forEach((o) => {
    if (computePreferred(o.id)) preferredIdSet.add(o.id);
  });

  const topLevelIdSet = new Set<number>();
  const collectTopLevel = (id: number) => {
    if (byId.get(id)?.isSoloRootNode) {
      (childrenByParent.get(id) ?? []).forEach(collectTopLevel);
    } else {
      topLevelIdSet.add(id);
    }
  };
  organizations.forEach((o) => {
    const isRoot = o.parentId == null || !byId.has(o.parentId);
    if (isRoot) collectTopLevel(o.id);
  });

  return {preferredIdSet, topLevelIdSet};
}

export function useLightOrganizationOptions(props: LightOrganizationFilterDataProps) {
  const {appConfig, getOptionDisabled, preferOrgSecurity = false} = props;
  const {data: organizations, isLoading, error} = useOrganizations(appConfig);
  const {data: myOrganizations, isLoading: myOrganizationsLoading} = useOrganizationsByOrgSecurity(appConfig, preferOrgSecurity);

  const {options, separatorId, flatSeparatorId} = useMemo(() => {
    const organizationsWithMetadata = ArrayUtils.computeHierarchyMetadata(organizations, (v) => ({id: v.id, parentId: v.parentId}));
    const optionsUnsorted = organizationsWithMetadata.map((v) => ({
      ...v,
      label: v.organizationLongName,
      disabled: getOptionDisabled && getOptionDisabled(v),
    }));

    const myIds = new Set((myOrganizations ?? []).map((o) => o.id));
    const useGrouping = preferOrgSecurity && myIds.size > 0;
    if (!useGrouping) {
      return {options: sortBy(optionsUnsorted, 'label'), separatorId: null as number | null, flatSeparatorId: null as number | null};
    }

    const {preferredIdSet, topLevelIdSet} = computeOrganizationGrouping(organizationsWithMetadata, myIds);
    const sorted = sortBy(optionsUnsorted, [(o) => (preferredIdSet.has(o.id) ? 0 : 1), 'label']);

    // A divider only makes sense when both groups are actually present at the top level.
    let hasPreferredTop = false;
    let hasOtherTop = false;
    topLevelIdSet.forEach((id) => {
      if (preferredIdSet.has(id)) hasPreferredTop = true;
      else hasOtherTop = true;
    });

    let separator: number | null = null;
    let flatSeparator: number | null = null;
    if (hasPreferredTop && hasOtherTop) {
      sorted.forEach((o) => {
        // Hierarchy mode: divider is drawn after a top-level node's whole subtree,
        // so anchor it to the last preferred top-level node.
        if (preferredIdSet.has(o.id) && topLevelIdSet.has(o.id)) separator = o.id;
        // Flat mode: options are a single sorted list (preferred first), so anchor
        // the divider to the last preferred option overall.
        if (preferredIdSet.has(o.id)) flatSeparator = o.id;
      });
    }
    return {options: sorted, separatorId: separator, flatSeparatorId: flatSeparator};
  }, [organizations, myOrganizations, getOptionDisabled, preferOrgSecurity]);

  return {options, isLoading: isLoading || myOrganizationsLoading, error, separatorId, flatSeparatorId};
}
export const LightOrganizationFilter2: React.FC<LightOrganizationFilter2Props> = (props) => {
  const {appConfig, getOptionDisabled, preferOrgSecurityDepartments = false, ...extraProps} = props;
  const {options, separatorId, flatSeparatorId} = useLightOrganizationOptions({
    appConfig,
    getOptionDisabled,
    preferOrgSecurity: preferOrgSecurityDepartments,
  });
  return (
    <LightHierarchyFilter
      {...extraProps}
      options={options}
      ignoreOption={(option) => option.original.isSoloRootNode}
      getNodeSeparator={
        separatorId == null
          ? extraProps.getNodeSeparator
          : ({node}) => (String(node.id) === String(separatorId) ? <hr /> : null)
      }
      getOptionSeparator={
        flatSeparatorId == null
          ? extraProps.getOptionSeparator
          : (option) => (String(option.id) === String(flatSeparatorId) ? <hr /> : null)
      }
    />
  );
};
